<template>
  <div v-if="isMultiTechEnabled" class="flex flex-col gap-3">
    <template v-if="sortedTechEntries.length">
      <TechnicianTimeline
        v-for="entry in sortedTechEntries"
        :key="entry.technicianProfileId"
        :appointment="appointment"
        :technician="entry.technician"
        :appointment-technician-entry="entry.atEntry"
        :expanded="appointment.status !== 'completed'"
        @refresh-appointment="emit('refresh-appointment')"
      />
    </template>
    <TechnicianTimeline
      v-else-if="appointment.technician?.technician_id"
      :appointment="appointment"
      :technician="appointment.technician"
      :expanded="appointment.status !== 'completed'"
      @refresh-appointment="emit('refresh-appointment')"
    />
    <Tooltip
      v-if="canEditTechnicians"
      :key="appointment.service_category.id"
      :options="options"
    >
      <template #trigger>
        <div
          data-cy="assign-technician-trigger"
          class="p-3 bg-white rounded-lg border hover:bg-blue-50 cursor-pointer flex items-center gap-2"
        >
          <i class="fa-light fa-plus text-blue-600" />
          <div class="text-lg font-medium">
            {{ assignLabel }}
          </div>
        </div>
      </template>
      <template #tooltip>
        <AssignedTechnicianForm
          :key="formKey"
          :appointment="appointment"
          @saved="emit('refresh-appointment')"
        />
      </template>
    </Tooltip>
  </div>
  <Tooltip v-else :key="appointment.service_category.id" :options="options">
    <template #trigger>
      <div
        data-cy="assign-technician-trigger"
        class="p-3 bg-white rounded-lg border hover:bg-blue-50 cursor-pointer"
      >
        <div class="text-lg font-medium">Select Technician</div>
        <div class="text-sm font-medium text-gray-500">
          Unassigned (Pending Auto-Assignment)
        </div>
      </div>
    </template>
    <template #tooltip>
      <AssignedTechnicianForm
        :key="formKey"
        :appointment="appointment"
        @saved="emit('refresh-appointment')"
      />
    </template>
  </Tooltip>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useFeatureFlag } from "@/lib/useFeatureFlag";

import Tooltip from "@/components/Tooltip.vue";
import { AppointmentV2 } from "@/typings/appointment";
import AssignedTechnicianForm from "./AssignedTechnicianForm.vue";
import TechnicianTimeline from "./TechnicianTimeline.vue";

const emit = defineEmits(["refresh-appointment"]);
const props = defineProps<{ appointment: AppointmentV2 }>();
const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");

const sortedTechEntries = computed(() => {
  const ats = props.appointment.appointment_technicians ?? [];
  const techs = props.appointment.technicians ?? [];
  return [...ats]
    .sort((a, b) => a.position - b.position)
    .map((at) => ({
      technicianProfileId: at.technician_profile_id,
      atEntry: at,
      technician: techs.find(
        (t) => t.technician_id === at.technician_profile_id
      )
    }))
    .filter((entry) => entry.technician);
});

const EDITABLE_STATUSES = ["pending", "accepted", "running"];
const canEditTechnicians = computed(() =>
  EDITABLE_STATUSES.includes(props.appointment.status)
);

const assignLabel = computed(() => {
  if (
    sortedTechEntries.value.length ||
    props.appointment.technician?.technician_id
  ) {
    return "Add/Remove Technicians";
  }
  return "Assign Technicians";
});

const formKey = ref(0);

const options = ref({
  appendTo: document.body,
  interactive: true,
  hideOnClick: true,
  distance: [0, -300],
  arrow: false,
  placement: "bottom-start",
  trigger: "click",
  theme: "actions-menu light",
  onShow: () => {
    formKey.value++;
  }
});
</script>

<style lang="scss" scoped></style>
